import {
  Button,
  Dialog,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { isEmpty } from 'lodash';
import { useGlobalCore } from '../../../../core/modules/global';
import { UPDATE_LOCATION_DIALOG } from '../../../../core/constants/dialog';
import Spinner from '../../Spinner';
import { useCompanyCore } from '../../../../core/modules/company';
import Swal from 'sweetalert2';
import { useMeCore } from '../../../../core/modules/me';
import { useLocationCore } from '../../../../core/modules/location';
import { Warehouse } from '../../../../core/interfaces/warehouse.interface';
import { PutLocationPayloadRequest } from '../../../../core/modules/location/interfaces/put-location';
import { useWarehouseCore } from '../../../../core/modules/warehouse';
import { useMasterCore } from '../../../../core/modules/master';
import { Type } from '../../../../core/interfaces/type.interface';

const schema = yup.object().shape({
  code: yup.string().required('Campo requerido'),
  description: yup.string().nullable(),
  level: yup.string().required('Campo requerido'),
  aisle: yup.string().required('Campo requerido'),
  column: yup.string().required('Campo requerido'),
  location_type_id: yup.number().required('Campo requerido'),
  warehouse_id: yup.number().required('Campo requerido'),
  channel_type_ids: yup.array().of(yup.number().required()).min(1, 'Debe seleccionar al menos un canal').required('Campo obligatorio'),
});

const UpdateDialog = () => {
  const { dialogs, closeDialog } = useGlobalCore();
  const { user } = useMeCore();
  const { warehouses } = useWarehouseCore();
  const { location, putLocation, deleteLocation, getLocations, setLocation } = useLocationCore();
  const { companyId } = useCompanyCore();
  const { locationTypes, channelTypes } = useMasterCore();

  const [loading, setLoading]  = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse | null>(null);

  const canWrite = useMemo(() => user && (user.privileges.superAdmin || user.privileges.writeLocation), [user]); 

  const { control, formState, handleSubmit, resetField, setValue } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
  });
  const { isValid, errors } = formState;

  // Obtener los canales válidos de la bodega seleccionada
  const validChannelTypes = selectedWarehouse 
    ? channelTypes.filter(channel => 
        selectedWarehouse.channel_type_ids && 
        selectedWarehouse.channel_type_ids.includes(channel.id)
      )
    : [];

  const resetForm = useCallback(() => {
    resetField('code');
    resetField('description');
    resetField('level');
    resetField('aisle');
    resetField('column');
    resetField('location_type_id');
    resetField('channel_type_ids');
    resetField('warehouse_id');
    setSelectedWarehouse(null);
    setLocation(null);
    closeDialog(UPDATE_LOCATION_DIALOG);
  }, [closeDialog, resetField, setLocation, setValue]);

  useEffect(() => {
    if (location) {
      setValue("code", location.code);
      setValue("description", location.description);
      setValue("level", location.level || '');
      setValue("aisle", location.aisle || '');
      setValue("column", location.column || '');
      setValue("location_type_id", location.location_type_id);
      setValue("channel_type_ids", location.channel_type_ids || []);
      setValue("warehouse_id", location.warehouse_id);
      
      // Establecer la bodega seleccionada
      const warehouse = warehouses.find(w => w.id === location.warehouse_id);
      setSelectedWarehouse(warehouse || null);
    }
  }, [location, setValue, warehouses]);

  const onSubmit = useCallback(
    (values: any) => {
      if (companyId && location) {
        // Ensure channel_type_ids is a valid number array and filter only valid channels for the selected warehouse
        let channelIds = Array.isArray(values.channel_type_ids)
          ? values.channel_type_ids.filter((id: any) => id !== undefined && id !== null)
          : [];

        // Filtrar solo los canales válidos para la bodega seleccionada
        if (selectedWarehouse && selectedWarehouse.channel_type_ids) {
          channelIds = channelIds.filter((id: number) => 
            selectedWarehouse.channel_type_ids.includes(id)
          );
        }

        const payload: PutLocationPayloadRequest = {
          ...values,
          channel_type_ids: channelIds
        };

        setLoading(true);
        putLocation(companyId, location.id, payload, () => {
          resetForm();
          getLocations(companyId);
          setLoading(false);
        }, () => {
          setLoading(false);
        });
      }
    },
    [companyId, location, putLocation, resetForm, getLocations, selectedWarehouse]
  );

  const onDelete = useCallback(() => {
    Swal.fire({
      title: "Confimar acción",
      text: `Eliminar la ubicación ${location?.code}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Si, eliminar!",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (result.isConfirmed && companyId && location) {
        setLoading(true);

        deleteLocation(
          companyId,
          location.id,
          () => {
            setLoading(false);
            getLocations(companyId);
            resetForm();
          },
          () => {
            setLoading(false);
          }
        );
      }
    });
  }, [companyId, deleteLocation, getLocations, location, resetForm]);

  return (
    <Dialog
      classes={{
        paper: 'm-8 sm:m-24 min-w-[350px]',
      }}
      onClose={resetForm}
      open={Array.isArray(dialogs) && dialogs.includes(UPDATE_LOCATION_DIALOG)}
    >
      <DialogContent>
        <div className="flex flex-auto flex-col py-16 px-0 sm:px-16">
          <div className="flex flex-col">
            <div className="mb-16 flex items-center">
              <Typography className="text-2xl font-bold truncate">
                Modificar Ubicación
              </Typography>
            </div>
      
            <div className="w-full max-w-3xl mt-20">
              <div className="flex flex-col space-y-32">
                <form noValidate onSubmit={handleSubmit(onSubmit)}>
                  <div className="mb-24">
                    <Controller
                      name="warehouse_id"
                      control={control}
                      render={({ field }) => (
                        <FormControl variant="outlined" fullWidth required>
                          <InputLabel id="warehouse-label">
                            Bodega
                          </InputLabel>
                          <Select
                            {...field}
                            labelId="warehouse-label"
                            disabled={loading}
                            label="Bodega"
                            fullWidth
                            required
                            onChange={(e) => {
                              field.onChange(e);
                              const warehouse = warehouses.find(w => w.id === e.target.value);
                              setSelectedWarehouse(warehouse || null);
                              
                              // Limpiar canales seleccionados cuando cambia la bodega
                              resetField('channel_type_ids');
                              
                              // También limpiar el valor en el formulario para asegurar consistencia
                              setValue('channel_type_ids', []);
                            }}
                          >
                            {warehouses.map((store: Warehouse) => (
                              <MenuItem key={store.id} value={store.id}>{store.code}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="code"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Código"
                          type="text"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.code}
                          helperText={errors?.code?.message}
                          required
                          fullWidth
                        />
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="aisle"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Pasillo"
                          type="number"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.aisle}
                          helperText={errors?.aisle?.message}
                          required
                          fullWidth
                          placeholder="Ej: 1,2,3"
                        />
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="level"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Nivel"
                          type="number"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.level}
                          helperText={errors?.level?.message}
                          required
                          fullWidth
                          placeholder="Ej: 1,2,3"
                        />
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="column"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Columna"
                          type="text"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.column}
                          helperText={errors?.column?.message}
                          required
                          fullWidth
                          placeholder="Ej: Columna 1, Estante 3"
                        />
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="location_type_id"
                      control={control}
                      render={({ field }) => (
                        <FormControl variant="outlined" fullWidth required>
                          <InputLabel id="location-type-label">
                            Tipo
                          </InputLabel>
                          <Select
                            {...field}
                            labelId="location-type-label"
                            disabled={loading}
                            label="Tipo"
                            fullWidth
                            required
                          >
                            {locationTypes.map((location: Type) => (
                              <MenuItem key={location.id} value={location.id}>{location.description}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="channel_type_ids"
                      control={control}
                      render={({ field }) => (
                        <FormControl variant="outlined" fullWidth>
                          <InputLabel id="channel-label" required>
                            Canales
                          </InputLabel>
                          <Select
                            {...field}
                            value={Array.isArray(field.value) ? field.value : []}
                            labelId="channel-label"
                            label="Canales"
                            disabled={loading || !selectedWarehouse}
                            required
                            multiple
                            input={<OutlinedInput label="Canales" />}
                            renderValue={(selected) => {
                              const selectedIds = selected as number[];
                              return selectedIds
                                .map((id) => validChannelTypes.find((type) => type.id === id)?.name)
                                .filter(Boolean)
                                .join(', ');
                            }}
                          >
                            {validChannelTypes.map((type) => (
                              <MenuItem key={type.id} value={type.id}>
                                <Checkbox checked={field.value?.includes(type.id)} />
                                <ListItemText primary={type.name} />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="description"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Descripción"
                          type="text"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.description}
                          helperText={errors?.description?.message}
                          fullWidth
                        />
                      )}
                    />
                  </div>

                  {canWrite && (
                    <div className="flex justify-center gap-4">
                      <Button
                        variant="contained"
                        color="error"
                        sx={{ borderRadius: 50 }}
                        disabled={loading}
                        type="button"
                        onClick={onDelete}
                      >
                        <Spinner show={loading} />
                        Eliminar
                      </Button>
                      <Button
                        variant="contained"
                        color="secondary"
                        sx={{ borderRadius: 50 }}
                        disabled={!isEmpty(errors) || !isValid || loading}
                        type="submit"
                        size="large"
                      >
                        <Spinner show={loading} />
                        Guardar
                      </Button>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateDialog;
